// src/page/StamprallyNew.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import { getConditionIconUrl } from '../utils/conditionIcons';
import ConditionIcon from '../components/ConditionIcon';
import SpotSelect from '../components/SpotSelect';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { DragOverlay } from '@dnd-kit/core';

// ========== 外部コンポーネント定義 ==========

// DragHandle component - only the handle is draggable
const DragHandle = ({ id }) => {
  const { attributes, listeners, setNodeRef } = useSortable({ id });
  return (
    <button
      ref={setNodeRef}
      className="btn btn-sm btn-light me-1"
      {...attributes}
      {...listeners}
      style={{ cursor: 'grab' }}
    >
      <i className="fas fa-grip-vertical"></i>
    </button>
  );
};

// SortableItem component - row is NOT draggable, only the handle
const SortableItem = ({ condition, index, errors, updateCondition, toggleCondition, removeCondition, openConditionModal }) => {
  // エラーチェック
  const nameError = errors[`condition_${condition.id}_name`];
  const conditionError = errors[`condition_${condition.id}`];
  const hasError = nameError || conditionError;

  return (
    <>
      <tr
        className={!condition.enabled ? 'table-secondary' : hasError ? 'table-danger' : ''}
      >
        <td>
          <DragHandle id={condition.id} />
          {index + 1}
        </td>
        <td style={{ width: '80px', textAlign: 'center' }}>
          <ConditionIcon condition={condition} size={40} />
        </td>
        <td>
          <input
            type="text"
            className={`form-control form-control-sm ${nameError ? 'is-invalid' : ''}`}
            value={condition.name}
            onChange={(e) => updateCondition(condition.id, { name: e.target.value })}
            placeholder="管理用名称"
          />
        </td>
        <td>
          <select
            className="form-select form-select-sm"
            value={condition.category}
            onChange={(e) => {
              const newCategory = e.target.value;
              const updatedCondition = {
                ...condition,
                category: newCategory,
                subCategory: '',
                settings: {}
              };
              updateCondition(condition.id, {
                category: newCategory,
                subCategory: '',
                settings: {},
                iconUrl: getConditionIconUrl(updatedCondition)
              });
            }}
          >
            <option value="">選択</option>
            <option value="app">アプリデータ</option>
            <option value="qr">QRコード</option>
            <option value="location">位置情報</option>
          </select>
        </td>
        <td>
          {condition.category === 'app' && (
            <select
              className="form-select form-select-sm"
              value={condition.subCategory}
              onChange={(e) => {
                const newSubCategory = e.target.value;
                const updatedCondition = {
                  ...condition,
                  subCategory: newSubCategory,
                  settings: {}
                };
                updateCondition(condition.id, {
                  subCategory: newSubCategory,
                  settings: {},
                  iconUrl: getConditionIconUrl(updatedCondition)
                });
              }}
            >
              <option value="">選択</option>
              <option value="member">会員データ</option>
              <option value="history">履歴（購入・利用）</option>
              <option value="achievement">実績系</option>
            </select>
          )}
          {condition.category === 'qr' && (
            <select
              className="form-select form-select-sm"
              value={condition.subCategory}
              onChange={(e) => {
                const newSubCategory = e.target.value;
                const updatedCondition = {
                  ...condition,
                  subCategory: newSubCategory,
                  settings: {}
                };
                updateCondition(condition.id, {
                  subCategory: newSubCategory,
                  settings: {},
                  iconUrl: getConditionIconUrl(updatedCondition)
                });
              }}
            >
              <option value="">選択</option>
              <option value="spot_qr">スポット行動QR</option>
              <option value="custom_qr">独自QR</option>
            </select>
          )}
          {condition.category === 'location' && (
            <select
              className="form-select form-select-sm"
              value={condition.subCategory}
              onChange={(e) => {
                const newSubCategory = e.target.value;
                const updatedCondition = {
                  ...condition,
                  subCategory: newSubCategory,
                  settings: {}
                };
                updateCondition(condition.id, {
                  subCategory: newSubCategory,
                  settings: {},
                  iconUrl: getConditionIconUrl(updatedCondition)
                });
              }}
            >
              <option value="">選択</option>
              <option value="permission">位置情報許可</option>
              <option value="area">エリア内GPS</option>
            </select>
          )}
        </td>
        <td className="text-center">
          <div className="form-check form-switch d-flex justify-content-center">
            <input
              type="checkbox"
              className="form-check-input"
              checked={condition.enabled}
              onChange={() => toggleCondition(condition.id)}
            />
          </div>
        </td>
        <td>
          <span
            title={!condition.category || !condition.subCategory ? 'カテゴリとタイプを選択してください' : ''}
            style={{ display: 'inline-block' }}
          >
            <button
              className="btn btn-sm btn-outline-primary me-1"
              onClick={() => openConditionModal(condition)}
              disabled={!condition.category || !condition.subCategory}
            >
              編集
            </button>
          </span>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => removeCondition(condition.id)}
          >
            削除
          </button>
        </td>
      </tr>
      {hasError && (
        <tr className="table-danger">
          <td colSpan="7" className="py-1">
            <div className="small text-danger">
              {nameError && <div>⚠️ {nameError}</div>}
              {conditionError && <div>⚠️ {conditionError}</div>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ========== メインコンポーネント ==========

export default function StamprallyNew() {
  useAuthGuard();
  const navigate = useNavigate();

  const MSG = {
    apiError: 'サーバーエラーが発生しました',
    saved: '登録しました',
    uploadError: '画像のアップロードに失敗しました。'
  };

  // ---------- タブ管理 ----------
  const [activeTab, setActiveTab] = useState('basic'); // basic | access | condition | reward | reference

  // ---------- 基本タブ（台紙ベース） ----------
  const [stamprallyName, setStamprallyName] = useState('');
  const [sheetImageFile, setSheetImageFile] = useState(null);
  const [sheetImagePreview, setSheetImagePreview] = useState('');
  const [sheetLayout, setSheetLayout] = useState('2'); // '2' = 2列, '3' = 3列
  const [description, setDescription] = useState('');
  const [achievementDescription, setAchievementDescription] = useState('');
  const [holdingStartDate, setHoldingStartDate] = useState('');
  const [holdingEndDate, setHoldingEndDate] = useState('');
  const [exchangeStartDate, setExchangeStartDate] = useState('');
  const [exchangeEndDate, setExchangeEndDate] = useState('');
  const [termsUrl, setTermsUrl] = useState('');

  // ---------- 閲覧制限タブ ----------
  const [adminEmails, setAdminEmails] = useState('');
  const [accessUserIds, setAccessUserIds] = useState('');

  // ---------- 達成条件タブ ----------
  const [achievementType, setAchievementType] = useState('all'); // 'all' | 'partial'
  const [requiredCount, setRequiredCount] = useState('');
  const [conditions, setConditions] = useState([]);

  // ---------- 特典設定タブ ----------
  const [rewardType, setRewardType] = useState('apply'); // 'apply' | 'store' | 'auto'
  const [rewardImageFile, setRewardImageFile] = useState(null);
  const [rewardImagePreview, setRewardImagePreview] = useState('');
  const [rewardDescription, setRewardDescription] = useState('');
  // 応募型
  const [requireAddress, setRequireAddress] = useState(false);
  // 店舗交換型
  const [exchangeSpots, setExchangeSpots] = useState([]);
  const [linkedCoupon, setLinkedCoupon] = useState('');
  // 自動付与型
  const [autoGrantType, setAutoGrantType] = useState('coupon'); // 'coupon' | 'point' | 'ticket'
  const [autoGrantTarget, setAutoGrantTarget] = useState('');

  // ---------- 状態管理 ----------
  const [errors, setErrors] = useState({});
  const [configErrors, setConfigErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const fileInputRef = useRef(null);
  const rewardFileInputRef = useRef(null);
  const holdingPickrRef = useRef(null);
  const exchangePickrRef = useRef(null);

  // ---------- Flatpickr初期化 ----------
  useEffect(() => {
    // 基本タブ以外の場合は何もしない
    if (activeTab !== 'basic' || !window.flatpickr) return;

    const ja = window.flatpickr.l10ns?.ja || {};

    // 既存のインスタンスを破棄
    holdingPickrRef.current?.destroy?.();
    exchangePickrRef.current?.destroy?.();

    // 開催期間
    holdingPickrRef.current = window.flatpickr('#holdingRange', {
      mode: 'range',
      dateFormat: 'Y/m/d',
      locale: { ...ja, rangeSeparator: ' ~ ' },
      defaultDate: holdingStartDate && holdingEndDate ? [holdingStartDate, holdingEndDate] : null,
      onChange: (dates) => {
        if (dates?.length === 2) {
          setHoldingStartDate(dates[0].toISOString());
          setHoldingEndDate(dates[1].toISOString());
        }
      }
    });

    // 交換期間
    exchangePickrRef.current = window.flatpickr('#exchangeRange', {
      mode: 'range',
      dateFormat: 'Y/m/d',
      locale: { ...ja, rangeSeparator: ' ~ ' },
      defaultDate: exchangeStartDate && exchangeEndDate ? [exchangeStartDate, exchangeEndDate] : null,
      onChange: (dates) => {
        if (dates?.length === 2) {
          setExchangeStartDate(dates[0].toISOString());
          setExchangeEndDate(dates[1].toISOString());
        }
      }
    });

    return () => {
      holdingPickrRef.current?.destroy?.();
      exchangePickrRef.current?.destroy?.();
    };
  }, [activeTab, holdingStartDate, holdingEndDate, exchangeStartDate, exchangeEndDate]);

  // ---------- 画像アップロード（台紙） ----------
  const setSingleImage = (file) => {
    if (!file?.type?.startsWith('image/')) {
      alert('画像ファイルを選択してください');
      return;
    }
    if (sheetImagePreview && sheetImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(sheetImagePreview);
    }
    setSheetImageFile(file);
    setSheetImagePreview(URL.createObjectURL(file));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) setSingleImage(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const removeImage = () => {
    if (sheetImagePreview && sheetImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(sheetImagePreview);
    }
    setSheetImageFile(null);
    setSheetImagePreview('');
  };

  // ---------- 画像アップロード（特典） ----------
  const setRewardImage = (file) => {
    if (!file?.type?.startsWith('image/')) {
      alert('画像ファイルを選択してください');
      return;
    }
    if (rewardImagePreview && rewardImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(rewardImagePreview);
    }
    setRewardImageFile(file);
    setRewardImagePreview(URL.createObjectURL(file));
  };

  const removeRewardImage = () => {
    if (rewardImagePreview && rewardImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(rewardImagePreview);
    }
    setRewardImageFile(null);
    setRewardImagePreview('');
  };

  // ---------- バリデーション ----------
  const validateBasic = () => {
    const err = {};
    // 下書き保存時も必須の項目
    if (!stamprallyName.trim()) {
      err.stamprallyName = 'スタンプラリー名は必須です';
    }

    // すべての条件で条件名、カテゴリ、タイプを必須に
    conditions.forEach((c) => {
      // 条件名は必須（共通）
      if (!c.name.trim()) {
        err[`condition_${c.id}_name`] = '条件名は必須です';
      }
      
      // カテゴリは必須（共通）
      if (!c.category) {
        err[`condition_${c.id}_category`] = 'カテゴリは必須です';
      }
      
      // タイプ（subCategory）は必須（共通）
      if (!c.subCategory) {
        err[`condition_${c.id}_subCategory`] = 'タイプは必須です';
      }
      
      // 有効フラグがONの場合: settingsも必須
      if (c.enabled) {
        if (!c.settings || Object.keys(c.settings).length === 0) {
          err[`condition_${c.id}_settings`] = '設定が未完了です';
        }
      }
    });

    setErrors(err);
    return err;
  };

  const validateForPublish = () => {
    const err = {};
    const configErrs = [];

    if (!sheetImageFile && !sheetImagePreview) {
      err.sheetImage = '台紙画像は公開時必須です';
      configErrs.push('台紙画像が未設定です');
    }
    if (!description.trim()) {
      err.description = '説明文は公開時必須です';
      configErrs.push('説明文が未設定です');
    }
    if (!achievementDescription.trim()) {
      err.achievementDescription = '達成条件の説明は公開時必須です';
      configErrs.push('達成条件の説明が未設定です');
    }
    if (!holdingStartDate || !holdingEndDate) {
      err.holdingPeriod = '開催期間は公開時必須です';
      configErrs.push('開催期間が未設定です');
    }
    if (!exchangeStartDate || !exchangeEndDate) {
      err.exchangePeriod = '特典交換期間は公開時必須です';
      configErrs.push('特典交換期間が未設定です');
    }
    if (!termsUrl.trim()) {
      err.termsUrl = '規約は公開時必須です';
      configErrs.push('規約が未設定です');
    }
    if (!rewardImageFile && !rewardImagePreview) {
      err.rewardImage = '特典画像は公開時必須です';
      configErrs.push('特典画像が未設定です');
    }
    if (!rewardDescription.trim()) {
      err.rewardDescription = '特典説明は公開時必須です';
      configErrs.push('特典説明が未設定です');
    }

    // Check ALL conditions - for publish, ALL must be enabled and complete
    const problematicConditions = conditions.filter(c => {
      // Include if disabled
      if (!c.enabled) return true;
      // Include if enabled but incomplete
      return !c.name.trim() || !c.category || !c.subCategory || !c.settings || Object.keys(c.settings).length === 0;
    });

    if (problematicConditions.length > 0) {
      problematicConditions.forEach((c) => {
        const conditionNum = c.sortOrder + 1;
        const issues = [];

        if (!c.enabled) {
          issues.push('無効');
        } else {
          if (!c.name.trim()) issues.push('名称未設定');
          if (!c.category) issues.push('カテゴリ未選択');
          if (!c.subCategory) issues.push('タイプ未選択');
          if (!c.settings || Object.keys(c.settings).length === 0) issues.push('設定未完了');
        }

        configErrs.push(`条件${conditionNum}: ${c.name || '(名称未設定)'} [${issues.join(', ')}]`);
      });

      err.conditions = `${problematicConditions.length}個の条件が未完了または無効です`;
    }

    // 日付バリデーション
    if (holdingStartDate && holdingEndDate) {
      const start = new Date(holdingStartDate);
      const end = new Date(holdingEndDate);
      if (start > end) {
        err.holdingPeriod = '開催期間の終了日は開始日より後である必要があります';
        configErrs.push('開催期間が不正です');
      }
    }

    if (exchangeStartDate && exchangeEndDate) {
      const start = new Date(exchangeStartDate);
      const end = new Date(exchangeEndDate);
      if (start > end) {
        err.exchangePeriod = '交換期間の終了日は開始日より後である必要があります';
        configErrs.push('交換期間が不正です');
      }
    }

    if (holdingStartDate && exchangeStartDate) {
      const holdStart = new Date(holdingStartDate);
      const exchStart = new Date(exchangeStartDate);
      if (exchStart < holdStart) {
        err.exchangePeriod = '交換開始日は開催開始日より前には設定できません';
        configErrs.push('交換期間が開催期間より前です');
      }
    }

    setErrors(err);
    setConfigErrors(configErrs);
    return { errors: err, configErrors: configErrs };
  };

  // ---------- 保存処理 ----------
  const handleSave = async (publish = false) => {
    if (publish) {
      const validation = validateForPublish();
      if (Object.keys(validation.errors).length > 0) {
        alert('設定不備があるため公開できません:\n' + validation.configErrors.join('\n'));
        return;
      }

    } else {
      const validation = validateBasic();
      if (Object.keys(validation).length > 0) {
        const errorMessages = [];
        
        // スタンプラリー名のエラー
        if (validation.stamprallyName) {
          errorMessages.push('・スタンプラリー名は必須です');
        }
        
        // 条件関連のエラーを集計
        const conditionsWithErrors = conditions.filter(c =>
          validation[`condition_${c.id}_name`] ||
          validation[`condition_${c.id}_category`] ||
          validation[`condition_${c.id}_subCategory`] ||
          validation[`condition_${c.id}_settings`]
        );
        
        conditionsWithErrors.forEach((c) => {
          const conditionNum = c.sortOrder + 1;
          if (validation[`condition_${c.id}_name`]) {
            errorMessages.push(`・条件${conditionNum}: 条件名は必須です`);
          }
          if (validation[`condition_${c.id}_category`]) {
            errorMessages.push(`・条件${conditionNum}: カテゴリは必須です`);
          }
          if (validation[`condition_${c.id}_subCategory`]) {
            errorMessages.push(`・条件${conditionNum}: タイプは必須です`);
          }
          if (validation[`condition_${c.id}_settings`]) {
            errorMessages.push(`・条件${conditionNum}: 設定が未完了です`);
          }
        });
        
        alert(`入力エラーがあります:\n${errorMessages.join('\n')}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      // TODO: API実装
      const token = localStorage.getItem('access_token');

      // データ送信（画像なし）
      const payload = {
        stamprallyName,
        sheetLayout,
        description,
        achievementDescription,
        termsUrl,
        adminEmails: adminEmails.split(',').map(s => s.trim()).filter(Boolean),
        accessUserIds: accessUserIds.split(',').map(s => s.trim()).filter(Boolean),
        achievementType,
        requiredCount: achievementType === 'partial' ? parseInt(requiredCount) : conditions.filter(c => c.enabled && c.category && c.subCategory && c.name).length,
        conditions: conditions.filter(c => {
          // 下書き保存時: 条件名、カテゴリ、タイプがあれば送信（settingsは任意）
          // 公開時: 有効フラグがONで、すべての項目が設定されている必要あり
          if (publish) {
            return c.enabled &&
                   c.name.trim() &&
                   c.category &&
                   c.subCategory &&
                   c.settings &&
                   Object.keys(c.settings).length > 0;
          }
          // 下書き時: 条件名、カテゴリ、タイプがあればOK（settingsは任意）
          return c.name.trim() && c.category && c.subCategory;
        }).map(c => {
          // 新規作成時はIDを送信しない（API側で自動生成）
          const { id, ...conditionWithoutId } = c;
          // settings内の数値フィールドを適切な型に変換
          if (conditionWithoutId.settings) {
            const settings = conditionWithoutId.settings;
            // 緯度・経度はfloat64に変換
            if (settings.latitude !== undefined && settings.latitude !== '') {
              settings.latitude = parseFloat(settings.latitude);
            }
            if (settings.longitude !== undefined && settings.longitude !== '') {
              settings.longitude = parseFloat(settings.longitude);
            }
            // その他の整数フィールドをintegerに変換
            if (settings.radius !== undefined && settings.radius !== '') {
              settings.radius = parseInt(settings.radius, 10);
            }
            if (settings.requiredCount !== undefined && settings.requiredCount !== '') {
              settings.requiredCount = parseInt(settings.requiredCount, 10);
            }
            if (settings.requiredPoints !== undefined && settings.requiredPoints !== '') {
              settings.requiredPoints = parseInt(settings.requiredPoints, 10);
            }
            if (settings.streakDays !== undefined && settings.streakDays !== '') {
              settings.streakDays = parseInt(settings.streakDays, 10);
            }
          }
          // Add icon URL based on condition type
          const iconUrl = getConditionIconUrl(c);
          return {
            ...conditionWithoutId,
            iconUrl
          };
        }),
        rewardType,
        rewardDescription,
        requireAddress,
        exchangeSpots,
        linkedCoupon,
        autoGrantType,
        autoGrantTarget,
        publishState: publish ? 'published' : 'draft',
        // 日付フィールドは値がある場合のみ送信
        ...(holdingStartDate && { holdingStartDate }),
        ...(holdingEndDate && { holdingEndDate }),
        ...(exchangeStartDate && { exchangeStartDate }),
        ...(exchangeEndDate && { exchangeEndDate })
      };

      const res = await fetch(`${API_BASE_URL}/v1/stamprallies`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error(MSG.apiError);

      const created = await res.json();
      const stamprallyId = created?.stamprallyId || created?.id;

      // 画像アップロード（作成後）
      let sheetImagePath = '';
      let rewardImagePath = '';

      if (sheetImageFile && stamprallyId) {
        const formData = new FormData();
        formData.append('file', sheetImageFile, sheetImageFile.name);
        formData.append('path', `stamprally/${stamprallyId}/sheet/`);
        const upRes = await fetch(`${API_BASE_URL}/v1/file`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        if (!upRes.ok) throw new Error(MSG.uploadError);
        const upData = await upRes.json();
        sheetImagePath = upData?.path || upData?.result || '';
      }

      if (rewardImageFile && stamprallyId) {
        const formData = new FormData();
        formData.append('file', rewardImageFile, rewardImageFile.name);
        formData.append('path', `stamprally/${stamprallyId}/reward/`);
        const upRes = await fetch(`${API_BASE_URL}/v1/file`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        if (!upRes.ok) throw new Error(MSG.uploadError);
        const upData = await upRes.json();
        rewardImagePath = upData?.path || upData?.result || '';
      }

      // 画像パスを更新
      if ((sheetImagePath || rewardImagePath) && stamprallyId) {
        const putRes = await fetch(`${API_BASE_URL}/v1/stamprallies/${stamprallyId}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...(sheetImagePath && { sheetImagePath }),
            ...(rewardImagePath && { rewardImagePath })
          })
        });
        if (!putRes.ok) throw new Error(MSG.apiError);
      }

      alert(MSG.saved);
      navigate('/stamprally');
    } catch (e) {
      alert(e.message || MSG.apiError);
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- 達成条件管理 ----------
  const addCondition = () => {
    const newCondition = {
      id: Date.now(),
      category: '', // 'app' | 'qr' | 'location'
      subCategory: '',
      name: '',
      displayName: '',
      description: '',
      enabled: false,
      sortOrder: conditions.length,
      settings: {}
    };
    setConditions([...conditions, newCondition]);
  };

  const updateCondition = (id, updates) => {
    setConditions(conditions.map(c => c.id === id ? { ...c, ...updates } : c));
  };

  const removeCondition = (id) => {
    setConditions(conditions.filter(c => c.id !== id));
  };

  const toggleCondition = (id) => {
    setConditions(conditions.map(c => c.id === id ? { ...c, enabled: !c.enabled } : c));
  };

  // ---------- ドラッグ＆ドロップ ----------
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [activeId, setActiveId] = useState(null);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (active.id !== over.id) {
      setConditions((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        
        // sortOrderを再計算
        return newItems.map((item, index) => ({
          ...item,
          sortOrder: index
        }));
      });
    }
  };

  const activeCondition = activeId ? conditions.find(c => c.id === activeId) : null;

  // ---------- 条件編集モーダル ----------
  const [editingCondition, setEditingCondition] = useState(null);
  const [modalSettings, setModalSettings] = useState({});
  const [modalErrors, setModalErrors] = useState({});

  const openConditionModal = (condition) => {
    setEditingCondition(condition);

    // Auto-set permission settings for location/permission conditions
    if (condition.category === 'location' && condition.subCategory === 'permission') {
      setModalSettings({ permission: true });
    } else {
      // Existing settings or empty object
      const initialSettings = { ...(condition.settings || {}) };

      // Auto-set period dates if period block is displayed and dates not set
      if (condition.category === 'app' && condition.subCategory !== 'member') {
        // Extract date with proper timezone handling
        const extractDate = (dateStr) => {
          if (!dateStr) return '';
          // Parse the full datetime with timezone and get local date components
          const date = new Date(dateStr);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        if (!initialSettings.startDate && holdingStartDate) {
          initialSettings.startDate = extractDate(holdingStartDate);
        }
        if (!initialSettings.endDate && holdingEndDate) {
          initialSettings.endDate = extractDate(holdingEndDate);
        }
      }

      setModalSettings(initialSettings);
    }

    setModalErrors({});
  };

  const closeConditionModal = () => {
    setEditingCondition(null);
    setModalSettings({});
    setModalErrors({});
  };

  const validateModalSettings = () => {
    const errors = {};
    const category = editingCondition?.category;
    const subCategory = editingCondition?.subCategory;
    
    // Location/Area validation
    if (category === 'location' && subCategory === 'area') {
      if (!modalSettings.spotId) {
        errors.spotId = 'スポットを選択してください';
      }
      if (!modalSettings.radius || modalSettings.radius === '') {
        errors.radius = '半径を選択してください';
      }
    }
    
    // App/Member validation
    if (category === 'app' && subCategory === 'member') {
      if (!modalSettings.memberField) {
        errors.memberField = '判定対象フィールドを選択してください';
      }
    }
    
    // App/History validation
    if (category === 'app' && subCategory === 'history') {
      if (!modalSettings.historyType) {
        errors.historyType = '条件種類を選択してください';
      }
      if (!modalSettings.voucherId) {
        errors.voucherId = '対象乗車券IDを入力してください';
      }
      if (modalSettings.historyType?.includes('count') && !modalSettings.requiredCount) {
        errors.requiredCount = '必要回数を入力してください';
      }
      // weekDays required for use_specific_date
      if (modalSettings.historyType === 'use_specific_date' && (!modalSettings.weekDays || modalSettings.weekDays.length === 0)) {
        errors.weekDays = '対象曜日を選択してください';
      }
      // startTime and endTime required for use_time_range
      if (modalSettings.historyType === 'use_time_range') {
        if (!modalSettings.startTime) {
          errors.startTime = '開始時間を入力してください';
        }
        if (!modalSettings.endTime) {
          errors.endTime = '終了時間を入力してください';
        }
      }
    }
    
    // App/Achievement validation
    if (category === 'app' && subCategory === 'achievement') {
      if (!modalSettings.achievementType) {
        errors.achievementType = '実績種別を選択してください';
      }
      
      if (modalSettings.achievementType === 'point') {
        if (!modalSettings.aquireType) {
          errors.aquireType = '獲得タイプを選択してください';
        }
        if (!modalSettings.pointType) {
          errors.pointType = 'ポイント種別を選択してください';
        }
        if (modalSettings.aquireType === 'date_range' && !modalSettings.requiredPoints) {
          errors.requiredPoints = '必要ポイント数を入力してください';
        }
        if (modalSettings.aquireType === 'streak' && !modalSettings.streakDays) {
          errors.streakDays = '連続日数を入力してください';
        }
        // weekDays required for weekdays
        if (modalSettings.aquireType === 'weekdays' && (!modalSettings.weekDays || modalSettings.weekDays.length === 0)) {
          errors.weekDays = '対象曜日を選択してください';
        }
        // startTime and endTime required for time_range
        if (modalSettings.aquireType === 'time_range') {
          if (!modalSettings.startTime) {
            errors.startTime = '開始時間を入力してください';
          }
          if (!modalSettings.endTime) {
            errors.endTime = '終了時間を入力してください';
          }
        }
      }
      
      // spotId required for coupon and action
      if ((modalSettings.achievementType === 'coupon' || modalSettings.achievementType === 'action') && !modalSettings.spotId) {
        errors.spotId = '対象スポットを選択してください';
      }
      
      if (modalSettings.achievementType === 'view') {
        if (!modalSettings.viewTarget) {
          errors.viewTarget = '閲覧対象を選択してください';
        }
        if (modalSettings.viewTarget && !modalSettings.viewTargetId) {
          errors.viewTargetId = '対象IDを入力してください';
        }
      }
    }
    
    // QR validation (uses qrCode field for both spot_qr and custom_qr)
    if (category === 'qr') {
      if (!modalSettings.qrCode) {
        errors.qrCode = subCategory === 'spot_qr' 
          ? 'スポットIDを入力してください' 
          : 'QRコード値を入力してください';
      }
    }
    
    return errors;
  };

  const saveConditionSettings = () => {
    const errors = validateModalSettings();
    
    if (Object.keys(errors).length > 0) {
      setModalErrors(errors);
      return;
    }
    
    if (editingCondition) {
      // Determine if this condition needs map flag
      const isMapCondition = (
        // アプリデータ／実績系／クーポン利用
        (editingCondition.category === 'app' && 
         editingCondition.subCategory === 'achievement' && 
         modalSettings.achievementType === 'coupon') ||
        // アプリデータ／実績系／行動ポイント
        (editingCondition.category === 'app' && 
         editingCondition.subCategory === 'achievement' && 
         modalSettings.achievementType === 'action') ||
        // QRコード／スポット行動QR
        (editingCondition.category === 'qr' && 
         editingCondition.subCategory === 'spot_qr') ||
        // 位置情報／エリア内GPS
        (editingCondition.category === 'location' && 
         editingCondition.subCategory === 'area')
      );
      
      // Add map flag based on condition type
      const settingsToSave = {
        ...modalSettings,
        map: isMapCondition ? true : false
      };
      
      // Calculate new icon URL based on updated condition
      const updatedCondition = {
        ...editingCondition,
        settings: settingsToSave
      };
      const newIconUrl = getConditionIconUrl(updatedCondition);
      
      updateCondition(editingCondition.id, { 
        settings: settingsToSave,
        iconUrl: newIconUrl
      });
      closeConditionModal();
    }
  };

  const updateModalSetting = (key, value) => {
    setModalSettings(prev => ({ ...prev, [key]: value }));
  };

  // ---------- aquireType変更時に関連フィールドをクリア ----------
  useEffect(() => {
    if (editingCondition?.subCategory === 'achievement' && modalSettings.achievementType === 'point') {
      if (modalSettings.aquireType !== 'date_range' && modalSettings.requiredPoints) {
        setModalSettings(prev => {
          const { requiredPoints, ...rest } = prev;
          return rest;
        });
      }
      if (modalSettings.aquireType !== 'streak' && modalSettings.streakDays) {
        setModalSettings(prev => {
          const { streakDays, ...rest } = prev;
          return rest;
        });
      }
    }
  }, [modalSettings.aquireType, editingCondition?.subCategory, modalSettings.achievementType]);

  // ---------- achievementType変更時に関連フィールドをクリア ----------
  useEffect(() => {
    if (editingCondition?.subCategory === 'achievement' && modalSettings.achievementType) {
      const currentType = modalSettings.achievementType;
      
      setModalSettings(prev => {
        const newSettings = { ...prev };
        
        // Fields by type
        const pointFields = ['aquireType', 'pointType', 'requiredPoints', 'streakDays'];
        const viewFields = ['viewTarget', 'viewTargetId'];
        
        // Clear point-specific fields when not point type
        if (currentType !== 'point') {
          pointFields.forEach(field => delete newSettings[field]);
        }
        
        // Clear spotId when not coupon or action type
        if (currentType !== 'coupon' && currentType !== 'action') {
          delete newSettings.spotId;
        }
        
        // Clear view-specific fields when not view type
        if (currentType !== 'view') {
          viewFields.forEach(field => delete newSettings[field]);
        }
        
        return newSettings;
      });
    }
  }, [modalSettings.achievementType, editingCondition?.subCategory]);

  // ---------- フォームヘルパー ----------
  const invalid = (k) => (errors[k] ? 'is-invalid' : '');
  const feedback = (k) => errors[k] ? <div className="invalid-feedback d-block">{errors[k]}</div> : null;

  return (
    <Layout>
      <div className="content">
        <div className="container-fluid p-4">
          {/* ヘッダー */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb mb-1">
                  <li className="breadcrumb-item"><a href="/stamprally">スタンプラリー</a></li>
                  <li className="breadcrumb-item active">新規登録</li>
                </ol>
              </nav>
              <h2 className="mb-0">スタンプラリー新規登録</h2>
            </div>
            <div className="d-flex gap-2">
              {configErrors.length > 0 && (
                <div className="alert alert-warning mb-0 py-2">
                  <small>⚠️ 設定不備: {configErrors.length}件</small>
                </div>
              )}
              <button
                className="btn btn-secondary"
                onClick={() => navigate('/stamprally')}
                disabled={submitting}
              >
                キャンセル
              </button>
              <button
                className="btn btn-outline-primary"
                onClick={() => handleSave(false)}
                disabled={submitting}
              >
                {submitting ? '保存中...' : '下書き保存'}
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleSave(true)}
                disabled={submitting}
              >
                {submitting ? '公開中...' : '公開'}
              </button>
            </div>
          </div>

          {/* タブナビゲーション */}
          <ul className="nav nav-tabs mb-4">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'basic' ? 'active' : ''}`}
                onClick={() => setActiveTab('basic')}
              >
                基本（台紙ベース）
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'access' ? 'active' : ''}`}
                onClick={() => setActiveTab('access')}
              >
                閲覧制限
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'condition' ? 'active' : ''}`}
                onClick={() => setActiveTab('condition')}
              >
                達成条件
                {conditions.filter(c => c.enabled).length > 0 && (
                  <span className="badge bg-primary ms-1">{conditions.filter(c => c.enabled).length}</span>
                )}
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'reward' ? 'active' : ''}`}
                onClick={() => setActiveTab('reward')}
              >
                特典設定
              </button>
            </li>
          </ul>

          {/* 基本タブ */}
          {activeTab === 'basic' && (
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">基本情報（台紙ベース）</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  {/* スタンプラリー名 */}
                  <div className="col-12 mb-4">
                    <label className="form-label fw-bold">スタンプラリー名 <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className={`form-control ${invalid('stamprallyName')}`}
                      value={stamprallyName}
                      onChange={(e) => setStamprallyName(e.target.value)}
                      placeholder="スタンプラリー名を入力"
                    />
                    {feedback('stamprallyName')}
                  </div>

                  {/* 台紙画像 */}
                  <div className="col-md-6 mb-4">
                    <label className="form-label fw-bold">台紙画像 <span className="text-muted">（公開時必須）</span></label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="d-none"
                      onChange={(e) => e.target.files?.[0] && setSingleImage(e.target.files[0])}
                    />
                    {sheetImagePreview ? (
                      <div className="d-flex align-items-start gap-3">
                        <img
                          src={sheetImagePreview}
                          alt="Preview"
                          className="img-fluid rounded border"
                          style={{ maxHeight: '300px', objectFit: 'cover' }}
                        />
                        <div className="flex-grow-1">
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => fileInputRef.current?.click()}
                            >
                              画像を変更
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={removeImage}
                            >
                              画像を削除
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`border rounded p-3 text-center ${isDragOver ? 'border-primary bg-light' : ''}`}
                        style={{ minHeight: '200px', cursor: 'pointer' }}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <div className="text-muted py-5">
                          <i className="fas fa-cloud-upload-alt fa-3x mb-2"></i>
                          <p className="mb-0">ドラッグ&ドロップまたはクリックで画像を選択</p>
                          <small>対応形式: JPG, PNG, GIF</small>
                        </div>
                      </div>
                    )}
                    {uploading && <div className="text-muted mt-2">アップロード中...</div>}
                    {feedback('sheetImage')}
                  </div>

                  {/* 台紙レイアウト */}
                  <div className="col-md-6 mb-4">
                    <label className="form-label fw-bold">台紙レイアウト</label>
                    <div className="d-flex gap-3 mb-3">
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="layout2"
                          checked={sheetLayout === '2'}
                          onChange={() => setSheetLayout('2')}
                        />
                        <label className="form-check-label" htmlFor="layout2">2列 × N行</label>
                      </div>
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="layout3"
                          checked={sheetLayout === '3'}
                          onChange={() => setSheetLayout('3')}
                        />
                        <label className="form-check-label" htmlFor="layout3">3列 × N行</label>
                      </div>
                    </div>
                    {/* グリッドプレビュー */}
                    <div className="border rounded p-3 bg-light">
                      <small className="text-muted d-block mb-2">プレビュー（{sheetLayout}列レイアウト）</small>
                      <div className="row g-2">
                        {Array.from({ length: 6 }).map((_, i) => (
                          <div key={i} className={sheetLayout === '2' ? 'col-6' : 'col-4'}>
                            <div className="bg-white border rounded p-2 text-center" style={{ height: '60px' }}>
                              <small className="text-muted">スタンプ{i + 1}</small>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* 説明文 */}
                  <div className="col-12 mb-3">
                    <label className="form-label fw-bold">説明文 <span className="text-muted">（公開時必須・最大2000文字）</span></label>
                    <textarea
                      className={`form-control ${invalid('description')}`}
                      rows="4"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="スタンプラリーの説明を入力"
                    />
                    <small className="text-muted">{description.length}/2000文字</small>
                    {feedback('description')}
                  </div>

                  {/* 達成条件の説明 */}
                  <div className="col-12 mb-3">
                    <label className="form-label fw-bold">達成条件の説明 <span className="text-muted">（公開時必須・最大2000文字）</span></label>
                    <textarea
                      className={`form-control ${invalid('achievementDescription')}`}
                      rows="3"
                      value={achievementDescription}
                      onChange={(e) => setAchievementDescription(e.target.value)}
                      placeholder="スタンプラリーの達成条件を説明"
                    />
                    <small className="text-muted">{achievementDescription.length}/2000文字</small>
                    {feedback('achievementDescription')}
                  </div>

                  {/* 開催期間 */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">開催期間 <span className="text-muted">（公開時必須）</span></label>
                    <input
                      id="holdingRange"
                      className={`form-control ${invalid('holdingPeriod')}`}
                      placeholder="開始日 ~ 終了日"
                    />
                    {feedback('holdingPeriod')}
                  </div>

                  {/* 特典交換期間 */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">特典交換期間 <span className="text-muted">（公開時必須）</span></label>
                    <input
                      id="exchangeRange"
                      className={`form-control ${invalid('exchangePeriod')}`}
                      placeholder="開始日 ~ 終了日"
                    />
                    {feedback('exchangePeriod')}
                  </div>

                  {/* 規約 */}
                  <div className="col-12 mb-3">
                    <label className="form-label fw-bold">規約URL <span className="text-muted">（公開時必須）</span></label>
                    <textarea
                      className={`form-control ${invalid('termsUrl')}`}
                      rows="2"
                      value={termsUrl}
                      onChange={(e) => setTermsUrl(e.target.value)}
                      placeholder="https://example.com/terms"
                    />
                    {feedback('termsUrl')}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 閲覧制限タブ */}
          {activeTab === 'access' && (
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">閲覧制限設定</h5>
              </div>
              <div className="card-body">
                <div className="alert alert-info">
                  <small>アクセスを制限して、一般公開とは別に関係者のみ、選考リリース確認用などに利用できます。</small>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">管理ユーザーメールアドレス</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={adminEmails}
                      onChange={(e) => setAdminEmails(e.target.value)}
                      placeholder="admin1@example.com, admin2@example.com"
                    />
                    <small className="text-muted">カンマ区切りで複数指定可能</small>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold">アクセスユーザーID</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={accessUserIds}
                      onChange={(e) => setAccessUserIds(e.target.value)}
                      placeholder="user001, user002, user003"
                    />
                    <small className="text-muted">カンマ区切りで複数指定可能</small>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 達成条件タブ */}
          {activeTab === 'condition' && (
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">達成条件設定</h5>
                <span className="badge bg-info">有効条件: {conditions.filter(c => c.enabled).length}件</span>
              </div>
              <div className="card-body">
                {/* 全体設定 */}
                <div className="row mb-4 p-3 bg-light rounded">
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold">達成方式</label>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="typeAll"
                          checked={achievementType === 'all'}
                          onChange={() => setAchievementType('all')}
                        />
                        <label className="form-check-label" htmlFor="typeAll">ALLクリア</label>
                      </div>
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="typePartial"
                          checked={achievementType === 'partial'}
                          onChange={() => setAchievementType('partial')}
                        />
                        <label className="form-check-label" htmlFor="typePartial">中間達成</label>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold">総条件数</label>
                    <input
                      type="text"
                      className="form-control"
                      value={conditions.filter(c => c.enabled).length}
                      disabled
                    />
                    <small className="text-muted">有効な個別条件の件数</small>
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-bold">必要達成数</label>
                    <input
                      type="number"
                      className="form-control"
                      value={achievementType === 'all' ? conditions.filter(c => c.enabled).length : requiredCount}
                      onChange={(e) => setRequiredCount(e.target.value)}
                      disabled={achievementType === 'all'}
                      min="1"
                      max={conditions.filter(c => c.enabled).length || 1}
                    />
                    <small className="text-muted">
                      {achievementType === 'all' ? 'ALLクリア時は自動設定' : `1〜${conditions.filter(c => c.enabled).length || 1}で指定`}
                    </small>
                  </div>
                </div>

                {/* 個別条件リスト */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6>個別条件リスト</h6>
                  <button className="btn btn-primary btn-sm" onClick={addCondition}>
                    <i className="fas fa-plus me-1"></i>条件を追加
                  </button>
                </div>

                {conditions.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <p>条件が設定されていません。「条件を追加」ボタンから追加してください。</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                    >
                      <table className="table table-bordered">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '80px' }}>順</th>
                            <th style={{ width: '80px' }}>アイコン</th>
                            <th style={{ width: '20%' }}>条件名</th>
                            <th style={{ width: '15%' }}>カテゴリ</th>
                            <th style={{ width: '20%' }}>タイプ</th>
                            <th style={{ width: '80px' }}>有効</th>
                            <th style={{ width: '120px' }}>操作</th>
                          </tr>
                        </thead>
                        <SortableContext
                          items={conditions.map(c => c.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <tbody>
                            {conditions.map((condition, index) => (
                              <SortableItem
                                key={condition.id}
                                condition={condition}
                                index={index}
                                errors={errors}
                                updateCondition={updateCondition}
                                toggleCondition={toggleCondition}
                                removeCondition={removeCondition}
                                openConditionModal={openConditionModal}
                              />
                            ))}
                          </tbody>
                        </SortableContext>
                      </table>
                      <DragOverlay>
                        {activeCondition ? (
                          <div className="card shadow-lg" style={{ 
                            opacity: 0.9, 
                            backgroundColor: '#fff',
                            border: '2px solid #0d6efd',
                            cursor: 'grabbing'
                          }}>
                            <div className="card-body py-2 px-3">
                              <div className="d-flex align-items-center">
                                <i className="fas fa-grip-vertical me-2 text-muted"></i>
                                <span className="fw-bold">{activeCondition.name || '（名称未設定）'}</span>
                                <span className="ms-2 badge bg-secondary">
                                  {activeCondition.category === 'app' && 'アプリデータ'}
                                  {activeCondition.category === 'qr' && 'QRコード'}
                                  {activeCondition.category === 'location' && '位置情報'}
                                  {!activeCondition.category && '未設定'}
                                </span>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </DragOverlay>
                    </DndContext>
                  </div>
                )}

                {errors.conditions && (
                  <div className="alert alert-danger">
                    <small>⚠️ {errors.conditions}</small>
                  </div>
                )}
                {!errors.conditions && conditions.filter(c => c.enabled && c.category && c.subCategory && c.name).length === 0 && (
                  <div className="alert alert-warning">
                    <small>⚠️ 有効な個別条件が0件の場合、公開できません。</small>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 特典設定タブ */}
          {activeTab === 'reward' && (
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">特典設定</h5>
              </div>
              <div className="card-body">
                {/* 特典タイプ */}
                <div className="mb-4">
                  <label className="form-label fw-bold">特典タイプ</label>
                  <div className="d-flex gap-4">
                    <div className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="rewardApply"
                        checked={rewardType === 'apply'}
                        onChange={() => setRewardType('apply')}
                      />
                      <label className="form-check-label" htmlFor="rewardApply">応募型</label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="rewardStore"
                        checked={rewardType === 'store'}
                        onChange={() => setRewardType('store')}
                      />
                      <label className="form-check-label" htmlFor="rewardStore">店舗交換型</label>
                    </div>
                    {/* 自動付与型は今フェーズでは非表示
                    <div className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="rewardAuto"
                        checked={rewardType === 'auto'}
                        onChange={() => setRewardType('auto')}
                      />
                      <label className="form-check-label" htmlFor="rewardAuto">自動付与型</label>
                    </div>
                    */}
                  </div>
                </div>

                {/* 特典画像 */}
                <div className="mb-4">
                  <label className="form-label fw-bold">特典画像 <span className="text-muted">（公開時必須）</span></label>
                  <input
                    ref={rewardFileInputRef}
                    type="file"
                    accept="image/*"
                    className="d-none"
                    onChange={(e) => e.target.files?.[0] && setRewardImage(e.target.files[0])}
                  />
                  {rewardImagePreview ? (
                    <div className="d-flex align-items-start gap-3">
                      <img
                        src={rewardImagePreview}
                        alt="Reward"
                        className="img-fluid rounded border"
                        style={{ maxHeight: '200px', objectFit: 'cover' }}
                      />
                      <div className="flex-grow-1">
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => rewardFileInputRef.current?.click()}
                          >
                            画像を変更
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={removeRewardImage}
                          >
                            画像を削除
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="border rounded p-3 text-center"
                      style={{ minHeight: '150px', cursor: 'pointer', maxWidth: '400px' }}
                      onClick={() => rewardFileInputRef.current?.click()}
                    >
                      <div className="text-muted py-4">
                        <i className="fas fa-image fa-2x mb-2"></i>
                        <p className="mb-0">クリックで画像を選択</p>
                      </div>
                    </div>
                  )}
                  {feedback('rewardImage')}
                </div>

                {/* 特典説明 */}
                <div className="mb-4">
                  <label className="form-label fw-bold">特典説明 <span className="text-muted">（公開時必須）</span></label>
                  <textarea
                    className={`form-control ${invalid('rewardDescription')}`}
                    rows="3"
                    value={rewardDescription}
                    onChange={(e) => setRewardDescription(e.target.value)}
                    placeholder="特典の内容を説明"
                  />
                  {feedback('rewardDescription')}
                </div>

                {/* タイプ別設定 */}
                {rewardType === 'apply' && (
                  <div className="p-3 border rounded bg-light">
                    <h6>応募型設定</h6>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="requireAddress"
                        checked={requireAddress}
                        onChange={(e) => setRequireAddress(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="requireAddress">
                        発送が必要なため、住所登録をスタンプラリーの参加条件にする
                      </label>
                    </div>
                  </div>
                )}

                {rewardType === 'store' && (
                  <div className="p-3 border rounded bg-light">
                    <h6>店舗交換型設定</h6>
                    <div className="mb-3">
                      <label className="form-label">交換店舗（スポット）</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={exchangeSpots.join(', ')}
                        onChange={(e) => setExchangeSpots(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                        placeholder="spot001, spot002, spot003"
                      />
                      <small className="text-muted">カンマ区切りで複数指定（TODO: react-selectで検索選択に変更）</small>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">連動クーポン</label>
                      <input
                        type="text"
                        className="form-control"
                        value={linkedCoupon}
                        onChange={(e) => setLinkedCoupon(e.target.value)}
                        placeholder="coupon001"
                      />
                    </div>
                  </div>
                )}

                {/* 自動付与型設定は今フェーズでは非表示
                {rewardType === 'auto' && (
                  <div className="p-3 border rounded bg-light">
                    <h6>自動付与型設定</h6>
                    <div className="mb-3">
                      <label className="form-label">付与内容</label>
                      <select
                        className="form-select"
                        value={autoGrantType}
                        onChange={(e) => setAutoGrantType(e.target.value)}
                      >
                        <option value="coupon">クーポン付与</option>
                        <option value="point">ポイント付与</option>
                        <option value="ticket">チケット付与</option>
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">付与対象ID</label>
                      <input
                        type="text"
                        className="form-control"
                        value={autoGrantTarget}
                        onChange={(e) => setAutoGrantTarget(e.target.value)}
                        placeholder={`${autoGrantType === 'coupon' ? 'coupon' : autoGrantType === 'point' ? 'point' : 'ticket'}001`}
                      />
                    </div>
                  </div>
                )}
                */}
              </div>
            </div>
          )}
        </div>

        {/* 条件編集モーダル */}
        {editingCondition && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">条件の詳細設定</h5>
                  <button type="button" className="btn-close" onClick={closeConditionModal}></button>
                </div>
                <div className="modal-body">
                  {/* 基本情報 */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">条件名（管理用）</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editingCondition.name}
                        onChange={(e) => updateCondition(editingCondition.id, { name: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">条件名（アプリ表示用）</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editingCondition.displayName}
                        onChange={(e) => updateCondition(editingCondition.id, { displayName: e.target.value })}
                        placeholder={editingCondition.name}
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">条件説明（任意）</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={editingCondition.description}
                      onChange={(e) => updateCondition(editingCondition.id, { description: e.target.value })}
                    />
                  </div>

                  {/* アイコンプレビュー */}
                  {(editingCondition.category && editingCondition.subCategory) && (
                    <div className="mb-3">
                      <div className="d-flex align-items-center gap-2">
                        <label className="form-label mb-0">アイコン</label>
                        <ConditionIcon 
                          condition={{
                            category: editingCondition.category,
                            subCategory: editingCondition.subCategory,
                            settings: modalSettings
                          }} 
                          size={40} 
                        />
                      </div>
                    </div>
                  )}

                  {/* カテゴリ別詳細設定 */}
                  {editingCondition.category === 'app' && (
                    <div className="border rounded p-3 mb-3">
                      <h6>アプリデータ詳細設定</h6>
                      
                      {editingCondition.subCategory === 'member' && (
                        <div>
                          <label className="form-label">
                            判定対象フィールド <span className="text-danger">*</span>
                          </label>
                          <select
                            className={`form-select ${modalErrors.memberField ? 'is-invalid' : ''}`}
                            value={modalSettings.memberField || ''}
                            onChange={(e) => {
                              updateModalSetting('memberField', e.target.value);
                              if (modalErrors.memberField) {
                                setModalErrors(prev => ({ ...prev, memberField: null }));
                              }
                            }}
                          >
                            <option value="">選択</option>
                            <option value="birthday">生年月日を登録している</option>
                            <option value="address">住所を登録している</option>
                            <option value="traffic_ic">交通IC番号を連携している</option>
                            <option value="push_notification">「Push通知を受け取る」に登録している</option>
                          </select>
                          {modalErrors.memberField && (
                            <div className="invalid-feedback">{modalErrors.memberField}</div>
                          )}
                          <small className="text-muted">参加ボタン押下時に判定されます</small>
                        </div>
                      )}

                      {editingCondition.subCategory === 'history' && (
                        <div>
                          <div className="mb-3">
                            <label className="form-label">
                              条件種類 <span className="text-danger">*</span>
                            </label>
                            <select
                              className={`form-select ${modalErrors.historyType ? 'is-invalid' : ''}`}
                              value={modalSettings.historyType || ''}
                              onChange={(e) => {
                                updateModalSetting('historyType', e.target.value);
                                if (modalErrors.historyType) {
                                  setModalErrors(prev => ({ ...prev, historyType: null }));
                                }
                              }}
                            >
                              <option value="">選択</option>
                              <option value="purchase_count">指定期間中に、指定の乗車券を、指定の枚数（回数）購入</option>
                              <option value="use_date_range">指定期間中に、指定の乗車券を利用</option>
                              <option value="use_specific_date">指定日付（特定曜日など）で、指定の乗車券を利用</option>
                              <option value="use_time_range">指定時間帯に、指定の乗車券を利用</option>
                            </select>
                            {modalErrors.historyType && (
                              <div className="invalid-feedback">{modalErrors.historyType}</div>
                            )}
                          </div>
                          <div className="mb-3">
                            <label className="form-label">
                              対象乗車券ID <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${modalErrors.voucherId ? 'is-invalid' : ''}`}
                              value={modalSettings.voucherId || ''}
                              onChange={(e) => {
                                updateModalSetting('voucherId', e.target.value);
                                if (modalErrors.voucherId) {
                                  setModalErrors(prev => ({ ...prev, voucherId: null }));
                                }
                              }}
                              placeholder="ticket001"
                            />
                            {modalErrors.voucherId && (
                              <div className="invalid-feedback">{modalErrors.voucherId}</div>
                            )}
                          </div>
                          {modalSettings.historyType?.includes('count') && (
                            <div className="mb-3">
                              <label className="form-label">
                                必要回数 <span className="text-danger">*</span>
                              </label>
                              <input
                                type="number"
                                className={`form-control ${modalErrors.requiredCount ? 'is-invalid' : ''}`}
                                value={modalSettings.requiredCount || ''}
                                onChange={(e) => {
                                  updateModalSetting('requiredCount', e.target.value);
                                  if (modalErrors.requiredCount) {
                                    setModalErrors(prev => ({ ...prev, requiredCount: null }));
                                  }
                                }}
                                min="1"
                              />
                              {modalErrors.requiredCount && (
                                <div className="invalid-feedback">{modalErrors.requiredCount}</div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {editingCondition.subCategory === 'achievement' && (
                        <div>
                          <div className="mb-3">
                            <label className="form-label">
                              実績種別 <span className="text-danger">*</span>
                            </label>
                            <select
                              className={`form-select ${modalErrors.achievementType ? 'is-invalid' : ''}`}
                              value={modalSettings.achievementType || ''}
                              onChange={(e) => {
                                updateModalSetting('achievementType', e.target.value);
                                if (modalErrors.achievementType) {
                                  setModalErrors(prev => ({ ...prev, achievementType: null }));
                                }
                              }}
                            >
                              <option value="">選択</option>
                              <option value="point">ポイント実績</option>
                              <option value="coupon">クーポン利用</option>
                              <option value="action">行動ポイント</option>
                              <option value="view">閲覧実績</option>
                            </select>
                            {modalErrors.achievementType && (
                              <div className="invalid-feedback">{modalErrors.achievementType}</div>
                            )}
                          </div>
                          {modalSettings.achievementType === 'point' && (
                            <>
                              <div className="mb-3">
                                <label className="form-label">
                                  獲得タイプ <span className="text-danger">*</span>
                                </label>
                                <select
                                  className={`form-select ${modalErrors.aquireType ? 'is-invalid' : ''}`}
                                  value={modalSettings.aquireType || ''}
                                  onChange={(e) => {
                                    updateModalSetting('aquireType', e.target.value);
                                    if (modalErrors.aquireType) {
                                      setModalErrors(prev => ({ ...prev, aquireType: null }));
                                    }
                                  }}
                                >
                                  <option value="">選択</option>
                                  <option value="date_range">指定期間中に、取得ポイントが指定値に達する</option>
                                  <option value="streak">指定期間中に、N日間以上連続でポイント取得</option>
                                  <option value="weekdays">指定日付（特定曜日など）でポイント取得</option>
                                  <option value="time_range">指定時間帯（〜朝7:00など）にポイント取得</option>
                                </select>
                                {modalErrors.aquireType && (
                                  <div className="invalid-feedback">{modalErrors.aquireType}</div>
                                )}
                              </div>
                              <div className="mb-3">
                                <label className="form-label">
                                  ポイント種別 <span className="text-danger">*</span>
                                </label>
                                <select
                                  className={`form-select ${modalErrors.pointType ? 'is-invalid' : ''}`}
                                  value={modalSettings.pointType || ''}
                                  onChange={(e) => {
                                    updateModalSetting('pointType', e.target.value);
                                    if (modalErrors.pointType) {
                                      setModalErrors(prev => ({ ...prev, pointType: null }));
                                    }
                                  }}
                                >
                                  <option value="">選択</option>
                                  <option value="launch">起動ポイント</option>
                                  <option value="action">行動ポイント</option>
                                </select>
                                {modalErrors.pointType && (
                                  <div className="invalid-feedback">{modalErrors.pointType}</div>
                                )}
                              </div>
                              {modalSettings.aquireType === 'date_range' && (
                                <div className="mb-3">
                                  <label className="form-label">
                                    必要ポイント数 <span className="text-danger">*</span>
                                  </label>
                                  <input
                                    type="number"
                                    className={`form-control ${modalErrors.requiredPoints ? 'is-invalid' : ''}`}
                                    value={modalSettings.requiredPoints || ''}
                                    onChange={(e) => {
                                      updateModalSetting('requiredPoints', e.target.value);
                                      if (modalErrors.requiredPoints) {
                                        setModalErrors(prev => ({ ...prev, requiredPoints: null }));
                                      }
                                    }}
                                    min="1"
                                  />
                                  {modalErrors.requiredPoints && (
                                    <div className="invalid-feedback">{modalErrors.requiredPoints}</div>
                                  )}
                                </div>
                              )}
                              {modalSettings.aquireType === 'streak' && (
                                <div className="mb-3">
                                  <label className="form-label">
                                    連続日数 <span className="text-danger">*</span>
                                  </label>
                                  <input
                                    type="number"
                                    className={`form-control ${modalErrors.streakDays ? 'is-invalid' : ''}`}
                                    value={modalSettings.streakDays || ''}
                                    onChange={(e) => {
                                      updateModalSetting('streakDays', e.target.value);
                                      if (modalErrors.streakDays) {
                                        setModalErrors(prev => ({ ...prev, streakDays: null }));
                                      }
                                    }}
                                    min="1"
                                    placeholder="N日間"
                                  />
                                  {modalErrors.streakDays && (
                                    <div className="invalid-feedback">{modalErrors.streakDays}</div>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                          {(modalSettings.achievementType === 'coupon' || modalSettings.achievementType === 'action') && (
                            <SpotSelect
                              value={modalSettings.spotId}
                              onChange={(spot) => {
                                if (spot) {
                                  // Save same fields as location/area
                                  updateModalSetting('spotId', spot.spotId);
                                  updateModalSetting('spotName', spot.spotName);
                                  updateModalSetting('spotAddress', spot.spotAddress || '');
                                  updateModalSetting('latitude', spot.latitude);
                                  updateModalSetting('longitude', spot.longitude);
                                } else {
                                  // Clear all fields
                                  updateModalSetting('spotId', '');
                                  updateModalSetting('spotName', '');
                                  updateModalSetting('spotAddress', '');
                                  updateModalSetting('latitude', '');
                                  updateModalSetting('longitude', '');
                                }
                                if (modalErrors.spotId) {
                                  setModalErrors(prev => ({ ...prev, spotId: null }));
                                }
                              }}
                              error={modalErrors.spotId}
                              label="対象スポット"
                              required
                              className="mb-3"
                            />
                          )}
                          {modalSettings.achievementType === 'view' && (
                            <>
                              <div className="mb-3">
                                <label className="form-label">
                                  閲覧対象 <span className="text-danger">*</span>
                                </label>
                                <select
                                  className={`form-select ${modalErrors.viewTarget ? 'is-invalid' : ''}`}
                                  value={modalSettings.viewTarget || ''}
                                  onChange={(e) => {
                                    updateModalSetting('viewTarget', e.target.value);
                                    if (modalErrors.viewTarget) {
                                      setModalErrors(prev => ({ ...prev, viewTarget: null }));
                                    }
                                  }}
                                >
                                  <option value="">選択</option>
                                  <option value="external">外部サイト</option>
                                  <option value="event">アプリ内イベント</option>
                                  <option value="news">ピックアップニュース</option>
                                  <option value="notice">お知らせ</option>
                                </select>
                                {modalErrors.viewTarget && (
                                  <div className="invalid-feedback">{modalErrors.viewTarget}</div>
                                )}
                              </div>
                              {modalSettings.viewTarget && (
                                <div className="mb-3">
                                  <label className="form-label">
                                    対象ID <span className="text-danger">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    className={`form-control ${modalErrors.viewTargetId ? 'is-invalid' : ''}`}
                                    value={modalSettings.viewTargetId || ''}
                                    onChange={(e) => {
                                      updateModalSetting('viewTargetId', e.target.value);
                                      if (modalErrors.viewTargetId) {
                                        setModalErrors(prev => ({ ...prev, viewTargetId: null }));
                                      }
                                    }}
                                    placeholder="target001"
                                  />
                                  {modalErrors.viewTargetId && (
                                    <div className="invalid-feedback">{modalErrors.viewTargetId}</div>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {editingCondition.category === 'qr' && (
                    <div className="border rounded p-3 mb-3">
                      <h6>QRコード詳細設定</h6>
                      <div className="mb-3">
                        <label className="form-label">QR種別</label>
                        <select
                          className="form-select"
                          value={modalSettings.qrType || editingCondition.subCategory}
                          onChange={(e) => updateModalSetting('qrType', e.target.value)}
                        >
                          <option value="spot_qr">スポット行動QR</option>
                          <option value="custom_qr">独自QR</option>
                        </select>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">
                          {modalSettings.qrType === 'spot_qr' ? 'スポットID' : 'QRコード値'} <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${modalErrors.qrCode ? 'is-invalid' : ''}`}
                          value={modalSettings.qrCode || ''}
                          onChange={(e) => {
                            updateModalSetting('qrCode', e.target.value);
                            if (modalErrors.qrCode) {
                              setModalErrors(prev => ({ ...prev, qrCode: null }));
                            }
                          }}
                          placeholder={modalSettings.qrType === 'spot_qr' ? 'spot001' : 'qr001'}
                        />
                        {modalErrors.qrCode && (
                          <div className="invalid-feedback">{modalErrors.qrCode}</div>
                        )}
                      </div>
                      <div className="mb-3">
                        <label className="form-label">設置場所情報（表示用・任意）</label>
                        <input
                          type="text"
                          className="form-control mb-2"
                          value={modalSettings.locationName || ''}
                          onChange={(e) => updateModalSetting('locationName', e.target.value)}
                          placeholder="施設名"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={modalSettings.locationAddress || ''}
                          onChange={(e) => updateModalSetting('locationAddress', e.target.value)}
                          placeholder="住所"
                        />
                      </div>
                    </div>
                  )}

                  {editingCondition.category === 'location' && (
                    <div className="border rounded p-3 mb-3">
                      <h6>位置情報詳細設定</h6>
                      {editingCondition.subCategory === 'permission' && (
                        <div className="alert alert-info">
                          <small>位置情報の取得許可があるかどうかを判定します。特別な設定は必要ありません。「適用」ボタンをクリックして保存してください。</small>
                        </div>
                      )}
                      {editingCondition.subCategory === 'area' && (
                        <>
                          {/* Spot Selection Dropdown */}
                          <SpotSelect
                            value={modalSettings.spotId}
                            onChange={(spot) => {
                              if (spot) {
                                updateModalSetting('spotId', spot.spotId);
                                updateModalSetting('spotName', spot.spotName);
                                updateModalSetting('spotAddress', spot.spotAddress || '');
                                updateModalSetting('latitude', spot.latitude);
                                updateModalSetting('longitude', spot.longitude);
                              } else {
                                // Clear all fields if no spot selected
                                updateModalSetting('spotId', '');
                                updateModalSetting('spotName', '');
                                updateModalSetting('spotAddress', '');
                                updateModalSetting('latitude', '');
                                updateModalSetting('longitude', '');
                              }

                              // Clear error
                              if (modalErrors.spotId) {
                                setModalErrors(prev => ({ ...prev, spotId: null }));
                              }
                            }}
                            error={modalErrors.spotId}
                            label="スポット選択"
                            required
                            className="mb-3"
                          />

                          <div className="mb-3">
                            <label className="form-label">
                              半径（m） <span className="text-danger">*</span>
                            </label>
                            <select
                              className={`form-select ${modalErrors.radius ? 'is-invalid' : ''}`}
                              value={modalSettings.radius || ''}
                              onChange={(e) => {
                                updateModalSetting('radius', e.target.value);
                                if (modalErrors.radius) {
                                  setModalErrors(prev => ({ ...prev, radius: null }));
                                }
                              }}
                            >
                              <option value="">選択</option>
                              <option value="50">50m</option>
                              <option value="100">100m</option>
                              <option value="200">200m</option>
                            </select>
                            {modalErrors.radius && (
                              <div className="invalid-feedback">{modalErrors.radius}</div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* 対象期間・曜日・時間帯（共通・任意） */}
                  {editingCondition.category === 'app' && editingCondition.subCategory !== 'member' && (
                  <div className="border rounded p-3">
                    <h6>対象期間・曜日・時間帯（任意）</h6>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">対象期間開始</label>
                        <input
                          type="date"
                          className="form-control"
                          value={modalSettings.startDate || ''}
                          onChange={(e) => updateModalSetting('startDate', e.target.value)}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">対象期間終了</label>
                        <input
                          type="date"
                          className="form-control"
                          value={modalSettings.endDate || ''}
                          onChange={(e) => updateModalSetting('endDate', e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        対象曜日
                        {((editingCondition?.subCategory === 'history' && modalSettings.historyType === 'use_specific_date') ||
                          (editingCondition?.subCategory === 'achievement' && modalSettings.achievementType === 'point' && modalSettings.aquireType === 'weekdays')) && (
                          <span className="text-danger">*</span>
                        )}
                      </label>
                      <div className="d-flex gap-3">
                        {['日', '月', '火', '水', '木', '金', '土'].map((day, idx) => (
                          <div className="form-check" key={day}>
                            <input
                              type="checkbox"
                              className={`form-check-input ${modalErrors.weekDays ? 'is-invalid' : ''}`}
                              id={`day${idx}`}
                              checked={(modalSettings.weekDays || []).includes(idx)}
                              onChange={(e) => {
                                const current = modalSettings.weekDays || [];
                                const updated = e.target.checked
                                  ? [...current, idx]
                                  : current.filter(d => d !== idx);
                                updateModalSetting('weekDays', updated);
                                if (modalErrors.weekDays) {
                                  setModalErrors(prev => ({ ...prev, weekDays: null }));
                                }
                              }}
                            />
                            <label className="form-check-label" htmlFor={`day${idx}`}>{day}</label>
                          </div>
                        ))}
                      </div>
                      {modalErrors.weekDays && (
                        <div className="invalid-feedback d-block">{modalErrors.weekDays}</div>
                      )}
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <label className="form-label">
                          対象時間帯（開始）
                          {((editingCondition?.subCategory === 'history' && modalSettings.historyType === 'use_time_range') ||
                            (editingCondition?.subCategory === 'achievement' && modalSettings.achievementType === 'point' && modalSettings.aquireType === 'time_range')) && (
                            <span className="text-danger">*</span>
                          )}
                        </label>
                        <input
                          type="time"
                          className={`form-control ${modalErrors.startTime ? 'is-invalid' : ''}`}
                          value={modalSettings.startTime || ''}
                          onChange={(e) => {
                            updateModalSetting('startTime', e.target.value);
                            if (modalErrors.startTime) {
                              setModalErrors(prev => ({ ...prev, startTime: null }));
                            }
                          }}
                        />
                        {modalErrors.startTime && (
                          <div className="invalid-feedback">{modalErrors.startTime}</div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">
                          対象時間帯（終了）
                          {((editingCondition?.subCategory === 'history' && modalSettings.historyType === 'use_time_range') ||
                            (editingCondition?.subCategory === 'achievement' && modalSettings.achievementType === 'point' && modalSettings.aquireType === 'time_range')) && (
                            <span className="text-danger">*</span>
                          )}
                        </label>
                        <input
                          type="time"
                          className={`form-control ${modalErrors.endTime ? 'is-invalid' : ''}`}
                          value={modalSettings.endTime || ''}
                          onChange={(e) => {
                            updateModalSetting('endTime', e.target.value);
                            if (modalErrors.endTime) {
                              setModalErrors(prev => ({ ...prev, endTime: null }));
                            }
                          }}
                        />
                        {modalErrors.endTime && (
                          <div className="invalid-feedback">{modalErrors.endTime}</div>
                        )}
                      </div>
                    </div>
                   </div>
                  )}
                </div>
                <div className="alert alert-light border mb-3 mx-0">
                  <small className="text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    ※この画面で「適用」後、メイン画面の「下書き保存」または「公開」ボタンで確定されます
                  </small>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeConditionModal}>キャンセル</button>
                  <button type="button" className="btn btn-primary" onClick={saveConditionSettings}>適用</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
